/**
 * Transfers page (Phase 3, §6.5). Two sections over the live transfer-detection
 * queues:
 *   1. "Erkannte Überweisungen" — unlinked candidate pairs with Verknüpfen
 *      (POST /api/transfers/link, optimistic card removal) and Ignorieren
 *      (client-side, session-scoped — no mutation).
 *   2. "Verknüpfte Überweisungen" — linked groups with Trennen
 *      (POST /api/transfers/{group_id}/unlink).
 * Mutations are disabled offline (never queued); a manual "Suchen" runs a full
 * auto-link pass.
 */
import { useMemo, useState } from 'react';
import { ArrowLeftRight, Search } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { ErrorState } from '../components/ui/ErrorState.tsx';
import { SkeletonList } from '../components/ui/Skeleton.tsx';
import { useOnlineStatus } from '../context/OnlineStatusContext.tsx';
import { useToast } from '../context/ToastContext.tsx';
import { ApiRequestError } from '../lib/api.ts';
import {
  useDetectTransfers,
  useLinkTransfer,
  useLinkedTransfers,
  useTransferCandidates,
  useUnlinkTransfer,
} from '../lib/hooks/useTransfers.ts';
import type { TransferCandidateOut } from '../lib/types.ts';
import { TransferCandidateCard } from './transfers/TransferCandidateCard.tsx';
import { LinkedTransferRow } from './transfers/LinkedTransferRow.tsx';

const WINDOW_DAYS = 4;

function pairKey(candidate: TransferCandidateOut): string {
  return `${candidate.out_txn.id}-${candidate.in_txn.id}`;
}

export default function TransfersPage() {
  const { online, guardOnline } = useOnlineStatus();
  const { toast } = useToast();

  const candidatesQuery = useTransferCandidates({ window_days: WINDOW_DAYS });
  const linkedQuery = useLinkedTransfers();
  const linkMutation = useLinkTransfer();
  const unlinkMutation = useUnlinkTransfer();
  const detectMutation = useDetectTransfers();

  // Session-scoped: pairs the user ignored, plus optimistically-linked pairs.
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);

  const visibleCandidates = useMemo(
    () => (candidatesQuery.data ?? []).filter((c) => !hidden.has(pairKey(c))),
    [candidatesQuery.data, hidden],
  );

  function hide(key: string) {
    setHidden((prev) => new Set(prev).add(key));
  }

  function unhide(key: string) {
    setHidden((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }

  function handleLink(candidate: TransferCandidateOut) {
    if (!guardOnline()) return;
    const key = pairKey(candidate);
    hide(key);
    linkMutation.mutate(
      {
        out_transaction_id: candidate.out_txn.id,
        in_transaction_id: candidate.in_txn.id,
      },
      {
        onSuccess: () => toast('Überweisung verknüpft.', { variant: 'success' }),
        onError: (error) => {
          unhide(key);
          toast(
            error instanceof ApiRequestError ? error.message : 'Verknüpfen fehlgeschlagen.',
            { variant: 'error' },
          );
        },
      },
    );
  }

  function handleIgnore(candidate: TransferCandidateOut) {
    hide(pairKey(candidate));
  }

  function handleUnlink(groupId: string) {
    if (!guardOnline()) return;
    setUnlinkingId(groupId);
    unlinkMutation.mutate(groupId, {
      onSuccess: () => toast('Verknüpfung getrennt.', { variant: 'success' }),
      onError: (error) =>
        toast(error instanceof ApiRequestError ? error.message : 'Trennen fehlgeschlagen.', {
          variant: 'error',
        }),
      onSettled: () => setUnlinkingId(null),
    });
  }

  function handleDetect() {
    if (!guardOnline()) return;
    detectMutation.mutate(undefined, {
      onSuccess: (result) =>
        toast(
          result.linked_count > 0
            ? `${result.linked_count} Überweisung(en) automatisch verknüpft.`
            : 'Keine neuen Überweisungen gefunden.',
          { variant: 'success' },
        ),
      onError: (error) =>
        toast(error instanceof ApiRequestError ? error.message : 'Suche fehlgeschlagen.', {
          variant: 'error',
        }),
    });
  }

  const linked = linkedQuery.data ?? [];

  return (
    <div>
      <PageHeader
        title="Überweisungen"
        subtitle="Umbuchungen zwischen deinen Konten erkennen und verknüpfen"
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={handleDetect}
            disabled={detectMutation.isPending || !online}
            title={online ? undefined : 'Offline nicht möglich'}
          >
            <Search className="h-4 w-4" />
            {detectMutation.isPending ? 'Suche …' : 'Suchen'}
          </Button>
        }
      />

      <div className="w-full space-y-10 px-4 py-6 sm:px-6">
        <section>
          <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
            Erkannte Überweisungen
          </h2>
          {candidatesQuery.isPending ? (
            <SkeletonList rows={3} />
          ) : candidatesQuery.isError ? (
            <ErrorState
              error={candidatesQuery.error}
              onRetry={() => void candidatesQuery.refetch()}
            />
          ) : visibleCandidates.length === 0 ? (
            <EmptyState
              icon={ArrowLeftRight}
              title="Keine Überweisungen erkannt"
              description="Passende Gegenbuchungen zwischen deinen Konten erscheinen hier automatisch."
            />
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800/70">
              {visibleCandidates.map((candidate) => (
                <TransferCandidateCard
                  key={pairKey(candidate)}
                  candidate={candidate}
                  onLink={handleLink}
                  onIgnore={handleIgnore}
                  linking={linkMutation.isPending}
                  online={online}
                />
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
            Verknüpfte Überweisungen
          </h2>
          {linkedQuery.isPending ? (
            <SkeletonList rows={2} />
          ) : linkedQuery.isError ? (
            <ErrorState error={linkedQuery.error} onRetry={() => void linkedQuery.refetch()} />
          ) : linked.length === 0 ? (
            <EmptyState
              icon={ArrowLeftRight}
              title="Noch nichts verknüpft"
              description="Verknüpfte Umbuchungen werden aus den Berichten herausgerechnet."
            />
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800/70">
              {linked.map((group) => (
                <LinkedTransferRow
                  key={group.transfer_group_id ?? group.legs.map((l) => l.id).join('-')}
                  group={group}
                  onUnlink={handleUnlink}
                  unlinking={unlinkingId === group.transfer_group_id}
                  online={online}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
